// Shared proposal-page sandbox blocks — rearrangeable structures with stable
// IDs for future e-sign field mapping. Archetypes hydrate into these starters.

import {
  createEmptyIntentFillField,
  createEmptyIntentSignatureParty,
  type IntentFillField,
  type IntentFillKind,
  type IntentSignatureParty,
} from "./proposalIntentLayout.js";

export const PROPOSAL_SANDBOX_BLOCK_TYPES = [
  "rich_text",
  "customer_sample",
  "signature_party",
  "fill_field",
  "cover_letterhead",
  "cover_closing",
  "calc_summary",
  "payment_milestones",
] as const;

export type ProposalSandboxBlockType = (typeof PROPOSAL_SANDBOX_BLOCK_TYPES)[number];

export const PROPOSAL_SANDBOX_BLOCK_LABELS: Record<ProposalSandboxBlockType, string> = {
  rich_text: "Text",
  customer_sample: "Customer sample",
  signature_party: "Signature party",
  fill_field: "Fill line",
  cover_letterhead: "Letter header",
  cover_closing: "Letter closing",
  calc_summary: "Cost / calc table",
  payment_milestones: "Payment schedule table",
};

export type ProposalSandboxTextRole =
  | "preamble"
  | "signature_intro"
  | "body"
  | "intro"
  | "footer"
  | null;

export const PROPOSAL_SANDBOX_TEXT_VARIANTS = ["paragraph", "header"] as const;
export type ProposalSandboxTextVariant = (typeof PROPOSAL_SANDBOX_TEXT_VARIANTS)[number];

export const PROPOSAL_SANDBOX_TEXT_VARIANT_LABELS: Record<ProposalSandboxTextVariant, string> = {
  paragraph: "Body text",
  header: "Header text",
};

type BlockBase = {
  id: string;
  type: ProposalSandboxBlockType;
  included?: boolean;
};

export type ProposalSandboxRichTextBlock = BlockBase & {
  type: "rich_text";
  text: string;
  role?: ProposalSandboxTextRole;
  /** Visual style in the PDF. Default paragraph. */
  variant?: ProposalSandboxTextVariant;
};

export type ProposalSandboxCustomerSampleBlock = BlockBase & {
  type: "customer_sample";
};

export type ProposalSandboxSignaturePartyBlock = BlockBase & {
  type: "signature_party";
  title: string;
  fields: IntentFillField[];
};

export type ProposalSandboxFillFieldBlock = BlockBase & {
  type: "fill_field";
  kind: IntentFillKind;
  label: string;
  variable_key?: string | null;
};

export type ProposalSandboxCoverLetterheadBlock = BlockBase & {
  type: "cover_letterhead";
};

export type ProposalSandboxCoverClosingBlock = BlockBase & {
  type: "cover_closing";
};

export type ProposalSandboxCalcSummaryBlock = BlockBase & {
  type: "calc_summary";
};

export type ProposalSandboxPaymentMilestonesBlock = BlockBase & {
  type: "payment_milestones";
};

export type ProposalSandboxBlock =
  | ProposalSandboxRichTextBlock
  | ProposalSandboxCustomerSampleBlock
  | ProposalSandboxSignaturePartyBlock
  | ProposalSandboxFillFieldBlock
  | ProposalSandboxCoverLetterheadBlock
  | ProposalSandboxCoverClosingBlock
  | ProposalSandboxCalcSummaryBlock
  | ProposalSandboxPaymentMilestonesBlock;

function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createRichTextBlock(args?: {
  text?: string;
  role?: ProposalSandboxTextRole;
  variant?: ProposalSandboxTextVariant;
  included?: boolean;
}): ProposalSandboxRichTextBlock {
  return {
    id: newId("text"),
    type: "rich_text",
    text: args?.text ?? "",
    role: args?.role ?? null,
    variant: args?.variant === "header" ? "header" : "paragraph",
    included: args?.included !== false,
  };
}

export function createCustomerSampleBlock(
  included = true,
): ProposalSandboxCustomerSampleBlock {
  return { id: newId("sample"), type: "customer_sample", included };
}

export function createSignaturePartyBlock(
  party?: IntentSignatureParty,
): ProposalSandboxSignaturePartyBlock {
  const p = party ?? createEmptyIntentSignatureParty();
  return {
    id: p.id || newId("party"),
    type: "signature_party",
    title: p.title,
    fields: p.fields,
    included: true,
  };
}

export function createFillFieldBlock(
  field?: IntentFillField,
): ProposalSandboxFillFieldBlock {
  const f = field ?? createEmptyIntentFillField("text");
  return {
    id: f.id || newId("fill"),
    type: "fill_field",
    kind: f.kind,
    label: f.label,
    variable_key: f.variable_key ?? null,
    included: true,
  };
}

export function createCoverLetterheadBlock(
  included = true,
): ProposalSandboxCoverLetterheadBlock {
  return { id: newId("letterhead"), type: "cover_letterhead", included };
}

export function createCoverClosingBlock(included = true): ProposalSandboxCoverClosingBlock {
  return { id: newId("closing"), type: "cover_closing", included };
}

export function createCalcSummaryBlock(included = true): ProposalSandboxCalcSummaryBlock {
  return { id: newId("calc"), type: "calc_summary", included };
}

export function createPaymentMilestonesBlock(
  included = true,
): ProposalSandboxPaymentMilestonesBlock {
  return { id: newId("pay"), type: "payment_milestones", included };
}

/** Normalize a jsonb blocks array (unknown types dropped). */
export function normalizeSandboxBlocksArray(raw: unknown): ProposalSandboxBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((b, i) => normalizeOneBlock(b, i))
    .filter((b): b is ProposalSandboxBlock => b != null);
}

function isTextRole(v: unknown): v is Exclude<ProposalSandboxTextRole, null> {
  return (
    v === "preamble" ||
    v === "signature_intro" ||
    v === "body" ||
    v === "intro" ||
    v === "footer"
  );
}

function normalizeOneBlock(raw: unknown, index: number): ProposalSandboxBlock | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const type = o.type;
  if (typeof type !== "string") return null;
  const id = typeof o.id === "string" && o.id.trim() ? o.id.trim() : `block_${index}`;
  const included = o.included !== false;

  switch (type) {
    case "rich_text":
      return {
        id,
        type: "rich_text",
        text: typeof o.text === "string" ? o.text : "",
        role: isTextRole(o.role) ? o.role : null,
        variant: o.variant === "header" ? "header" : "paragraph",
        included,
      };
    case "customer_sample":
      return { id, type: "customer_sample", included };
    case "cover_letterhead":
      return { id, type: "cover_letterhead", included };
    case "cover_closing":
      return { id, type: "cover_closing", included };
    case "calc_summary":
      return { id, type: "calc_summary", included };
    case "payment_milestones":
      return { id, type: "payment_milestones", included };
    case "signature_party": {
      const fieldsRaw = Array.isArray(o.fields) ? o.fields : [];
      const fields = fieldsRaw
        .map((f, i) => normalizeFillLike(f, i))
        .filter((f): f is IntentFillField => f != null);
      return {
        id,
        type: "signature_party",
        title:
          typeof o.title === "string" && o.title.trim() ? o.title.trim() : "Signature",
        fields:
          fields.length > 0
            ? fields
            : [
                createEmptyIntentFillField("signature"),
                createEmptyIntentFillField("date"),
              ],
        included,
      };
    }
    case "fill_field": {
      const field = normalizeFillLike(
        { id, kind: o.kind, label: o.label, variable_key: o.variable_key },
        index,
      );
      if (!field) return null;
      return {
        id: field.id,
        type: "fill_field",
        kind: field.kind,
        label: field.label,
        variable_key: field.variable_key,
        included,
      };
    }
    default:
      return null;
  }
}

function normalizeFillLike(raw: unknown, index: number): IntentFillField | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const kind = o.kind;
  if (
    kind !== "signature" &&
    kind !== "initials" &&
    kind !== "date" &&
    kind !== "text"
  ) {
    return null;
  }
  return {
    id: typeof o.id === "string" && o.id.trim() ? o.id.trim() : `field_${index}`,
    kind,
    label: typeof o.label === "string" && o.label.trim() ? o.label.trim() : kind,
    variable_key:
      typeof o.variable_key === "string" && o.variable_key.trim()
        ? o.variable_key.trim()
        : null,
  };
}

export function findRichTextByRole(
  blocks: readonly ProposalSandboxBlock[],
  role: Exclude<ProposalSandboxTextRole, null>,
): ProposalSandboxRichTextBlock | null {
  for (const b of blocks) {
    if (b.type === "rich_text" && b.role === role) return b;
  }
  return null;
}

/** PDF style key for a rich_text block (header vs footer note vs body). */
export function sandboxRichTextStyleKind(
  block: ProposalSandboxRichTextBlock,
): "header" | "footer" | "paragraph" {
  if (block.variant === "header") return "header";
  if (block.role === "footer") return "footer";
  return "paragraph";
}

export function sandboxBlocksHaveContent(
  blocks: readonly ProposalSandboxBlock[],
): boolean {
  return blocks.some((b) => b.included !== false);
}

export function moveSandboxBlock(
  blocks: readonly ProposalSandboxBlock[],
  blockId: string,
  direction: "up" | "down",
): ProposalSandboxBlock[] {
  const next = [...blocks];
  const idx = next.findIndex((b) => b.id === blockId);
  if (idx < 0) return next;
  const swap = direction === "up" ? idx - 1 : idx + 1;
  if (swap < 0 || swap >= next.length) return next;
  [next[idx], next[swap]] = [next[swap]!, next[idx]!];
  return next;
}
